/**
 * Pure-function logic for the "Lean & Clean" navigation visibility layer.
 *
 * Orthogonal to the `enabled_features` system:
 *   - `enabled_features` answers "is this feature available for this customer?"
 *   - `nav_overrides` answers "how prominent should it appear in the UI?"
 *
 * A customer can have a feature enabled (e.g. `warteliste`) but still see it
 * tucked away under "Mehr…" instead of in the main sidebar. The two concerns
 * are deliberately split so the feature lifecycle (billing, permissions,
 * data access) never gets entangled with pure UI chrome decisions.
 *
 * This module is pure: no React, no Supabase, no imports from `@/hooks`.
 * That keeps it unit-testable and cheap to import from both the sidebar
 * renderer and the admin settings page.
 */

import type { RouteModule, Scope, Visibility } from './types';

/**
 * The two real organization setting-types in the database. `Scope` adds
 * `'both'` on top as a shortcut for routes that apply to either setting,
 * but a concrete org is always exactly one of these values.
 */
export type OrgSettingType = Exclude<Scope, 'both'>;

/**
 * Per-org override map, persisted as JSON in `organizations.nav_overrides`.
 *
 * Shape: `{ "<route-id>": "primary" | "secondary" | "hidden" }`
 *
 * Admins edit this via Settings → Workspace anpassen. A `null` value in
 * the DB means: fall through to code-side defaults below.
 */
export type NavOverrides = Readonly<Record<string, Visibility>>;

/**
 * The whitelist of route ids that appear in the slim main sidebar by
 * default. Kept intentionally small (6 entries) so the "Lean & Clean"
 * promise holds. This is the *praxis* core — the surfaces an ambulatory
 * therapist touches every day:
 *   Dashboard · Patienten · Kalender · Nachrichten · Ernährungspläne · Rechnungen
 *
 * Phase 1 (designte Default-Navigation) — "Therapie vor Reporting":
 * `nutrition-plans` (das zentrale Therapie-Artefakt) ersetzt `reporting`
 * (Auswertungen) im primären Core; Auswertungen fällt unter "Mehr…".
 * `nutrition-plans` ist `scope: 'praxis'`, taucht also nur in der Praxis
 * primär auf; Klinik-Primäreinträge (Stationen/Konsile/KPI/NutriScreen)
 * deklarieren ihre Prominenz inline über `nav.visibility: 'primary'`.
 *
 * Everything else with a nav entry defaults to `secondary` (lives under
 * the "Mehr…" accordion). Admin-footer entries default to `hidden` in
 * the visibility layer because they render via a dedicated admin footer
 * component, not the main/secondary list.
 *
 * Per-org overrides can promote any route to `primary` or demote it to
 * `hidden`, but the admin UI should make it clear that inflating the
 * primary list defeats the purpose.
 */
export const CORE_ROUTE_IDS: readonly string[] = [
  'dashboard',
  'patients',
  'calendar',
  'messages',
  // `nutrition-plans` is `scope: 'praxis'` — the core ambulatory therapy
  // deliverable. Replaces `reporting` in the primary core (Phase 1:
  // therapy over reporting); Auswertungen now lives under "Mehr…".
  'nutrition-plans',
  // `invoices` is `scope: 'praxis'`, so it only ends up in the praxis
  // primary list; klinik orgs never see it. Keeps the praxis primary
  // row at six items (Dashboard / Patienten / Kalender / Nachrichten /
  // Ernährungspläne / Rechnungen) — billing is the "sixth most-used"
  // surface for ambulatory practices.
  'invoices',
];

/**
 * The corresponding whitelist for the patient-detail tab system. These
 * tabs are always shown at the top level of the tab bar in the patient
 * view (as long as their feature flag is enabled). Used by the patient
 * tab renderer once PR 1.3 lands; defined here so the whole visibility
 * concern lives in one file.
 *
 * Mirrors the "9 Haupt-Tabs" from the refactor plan.
 */
export const CORE_PATIENT_TAB_IDS: readonly string[] = [
  'overview',
  'beratung',          // new container tab (PR 1.3)
  'aktivitaeten',      // Aktivitäten-Verlauf (Plattform-Durchgang)
  'messwerte',         // new container tab (PR 1.3)
  'plan',              // stays top-level; its sub-tabs (diary) wrap inside
  'analyse',           // new container tab (PR 1.3)
  'anamnese-fragebogen', // new container tab (PR 1.3)
  'verordnungen-aufgaben', // new container tab (PR 1.3)
  'documents',
  'nachrichten',       // Nachrichten-Thread (Plattform-Durchgang)
  'klinik',            // new container tab (PR 1.3)
];

/**
 * Default visibility for a route, derived from its registry entry alone.
 *
 * Rules (in order):
 *   1. Route id in `CORE_ROUTE_IDS` → `primary`
 *   2. Admin-section entry (`nav.section === 'admin'`)
 *      → `hidden` (the admin footer renders these via a dedicated path)
 *   3. Everything else with a nav entry → `secondary`
 *   4. Routes without a nav entry are not navigable at all → `hidden`
 *
 * An explicit `nav.visibility` in the registry wins over rules 1-3.
 * Per-org `nav_overrides` win over everything — see `resolveRouteVisibility`.
 */
export function deriveRouteVisibility(route: RouteModule): Visibility {
  if (!route.nav) return 'hidden';
  if (route.nav.visibility) return route.nav.visibility;
  if (CORE_ROUTE_IDS.includes(route.id)) return 'primary';
  if (route.nav.section === 'admin') return 'hidden';
  return 'secondary';
}

/**
 * Resolve a route's visibility after applying the per-org override map.
 *
 * The override map is typed loosely as `unknown` because it comes straight
 * from a Supabase `jsonb` column; we validate the value before returning
 * it and silently fall through to the default on malformed entries.
 */
export function resolveRouteVisibility(
  route: RouteModule,
  overrides: NavOverrides | null | undefined,
): Visibility {
  const override = overrides?.[route.id];
  if (override === 'primary' || override === 'secondary' || override === 'hidden') {
    return override;
  }
  return deriveRouteVisibility(route);
}

/**
 * Parse the raw `jsonb` value from `organizations.nav_overrides` into a
 * typed, validated `NavOverrides` map. Silently drops keys whose values
 * are not one of the three legal `Visibility` strings — better to fall
 * through to defaults than to crash the sidebar on bad data.
 */
export function parseNavOverrides(raw: unknown): NavOverrides | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, Visibility> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === 'primary' || value === 'secondary' || value === 'hidden') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Filter a list of routes into `{ primary, secondary }` for rendering in
 * the sidebar. Routes with resolved visibility `hidden` are excluded
 * entirely; admin-footer rendering is a separate concern handled by the
 * sidebar component itself.
 */
export function splitRoutesByVisibility(
  routes: readonly RouteModule[],
  overrides: NavOverrides | null | undefined,
): { primary: RouteModule[]; secondary: RouteModule[] } {
  const { primary, secondary } = partitionRoutesByVisibility(routes, overrides);
  return { primary, secondary };
}

/**
 * Like {@link splitRoutesByVisibility} but also returns the `hidden`
 * bucket. The sidebar renderer uses this so superadmins can still reach
 * routes that an org admin has demoted to `hidden` — the plan requires
 * that they "always see everything in the Secondary area, regardless of
 * `nav_overrides`". Normal users ignore the `hidden` bucket entirely.
 *
 * The relative order inside each bucket is preserved from the input,
 * so callers that have already sorted by `nav.order` keep that order.
 */
export function partitionRoutesByVisibility(
  routes: readonly RouteModule[],
  overrides: NavOverrides | null | undefined,
): { primary: RouteModule[]; secondary: RouteModule[]; hidden: RouteModule[] } {
  const primary: RouteModule[] = [];
  const secondary: RouteModule[] = [];
  const hidden: RouteModule[] = [];
  for (const route of routes) {
    if (!route.nav) continue;
    const v = resolveRouteVisibility(route, overrides);
    if (v === 'primary') primary.push(route);
    else if (v === 'secondary') secondary.push(route);
    else hidden.push(route);
  }
  return { primary, secondary, hidden };
}

/**
 * Immutably merge a single `<routeId, visibility>` pair into the current
 * `nav_overrides` map. Passing `null` for `visibility` removes the
 * override entirely and falls through to the code-side default.
 *
 * Returns a new object; never mutates the input. Intended for the
 * admin "Workspace anpassen" UI (PR 1.4) where a dropdown change
 * produces a single patched map to persist back to Supabase.
 */
export function mergeNavOverride(
  current: NavOverrides | null | undefined,
  routeId: string,
  visibility: Visibility | null,
): NavOverrides {
  const next: Record<string, Visibility> = { ...(current ?? {}) };
  if (visibility === null) {
    delete next[routeId];
  } else {
    next[routeId] = visibility;
  }
  return next;
}

/**
 * Pure helper that mirrors the filter + split logic the real
 * `<SidebarNav />` applies — exported so the admin "Workspace anpassen"
 * preview can render an accurate "this is what your users will see"
 * mini sidebar without duplicating the gating rules.
 *
 * Returns three buckets:
 *   - `primary`   — flat items at the top of the sidebar
 *   - `secondary` — items collapsed behind the "Mehr…" accordion
 *   - `admin`     — items in the admin footer (never affected by
 *                   the visibility layer)
 *
 * The `enabledFeatures` parameter takes a loose `readonly string[]`
 * rather than the strict `Feature` tuple so this module stays
 * dependency-free and testable without importing every feature key.
 */
export interface SidebarLayout {
  primary: RouteModule[];
  secondary: RouteModule[];
  admin: RouteModule[];
}

export function computeSidebarLayout(args: {
  routes: readonly RouteModule[];
  enabledFeatures: readonly string[];
  navOverrides: NavOverrides | null | undefined;
  role: string | undefined;
  isSuperadmin: boolean;
  /**
   * The organization's `setting_type`. When provided, routes with a
   * `scope` that does not match are filtered out (routes with scope
   * `'both'` or no scope at all stay visible). Superadmins bypass this
   * filter. When `undefined`, no scope filtering happens — useful for
   * tests and for contexts without org data.
   */
  settingType?: OrgSettingType;
}): SidebarLayout {
  const byOrder = (a: RouteModule, b: RouteModule) =>
    (a.nav?.order ?? Number.MAX_SAFE_INTEGER) -
    (b.nav?.order ?? Number.MAX_SAFE_INTEGER);

  const accessible = args.routes.filter((route) => {
    if (!route.nav) return false;
    if (route.feature && !args.enabledFeatures.includes(route.feature)) return false;
    if (route.roles && route.roles.length > 0) {
      if (!args.role || !route.roles.includes(args.role as never)) return false;
    }
    if (
      args.settingType &&
      route.scope &&
      route.scope !== 'both' &&
      route.scope !== args.settingType
    ) {
      return false;
    }
    return true;
  });

  const main = accessible.filter((r) => (r.nav?.section ?? 'main') === 'main');
  const adminItems = accessible
    .filter((r) => r.nav?.section === 'admin')
    .slice()
    .sort(byOrder);

  const { primary, secondary, hidden } = partitionRoutesByVisibility(
    main,
    args.navOverrides,
  );

  // Superadmins keep access to routes an org admin has demoted to
  // `hidden`, mirroring `<SidebarNav />`'s behaviour.
  const effectiveSecondary = args.isSuperadmin
    ? [...secondary, ...hidden]
    : secondary;

  return {
    primary: primary.slice().sort(byOrder),
    secondary: effectiveSecondary.slice().sort(byOrder),
    admin: adminItems,
  };
}
